import { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    alt: string;
    className?: string;
    containerClassName?: string;
}

export function OptimizedImage({
    src,
    alt,
    className,
    containerClassName,
    ...props
}: OptimizedImageProps) {
    const {
        srcSet,
        sizes,
        loading,
        width,
        height,
        fetchPriority,
        ...imgProps
    } = props;
    const [isLoaded, setIsLoaded] = useState(true);
    const [error, setError] = useState(false);
    const previousSrc = useRef(src);

    // Reset state when src changes
    useEffect(() => {
        if (previousSrc.current === src) return;

        previousSrc.current = src;
        setIsLoaded(false);
        setError(false);
    }, [src]);

    const responsiveSource = getResponsiveSource(src);

    return (
        <div className={cn("relative overflow-hidden bg-gray-100", containerClassName)}>
            {/* Skeleton Loading State */}
            {!isLoaded && !error && (
                <div className="absolute inset-0 z-10 bg-gray-200 animate-pulse" />
            )}

            <img
                src={responsiveSource.src}
                srcSet={srcSet || responsiveSource.srcSet}
                sizes={sizes || responsiveSource.sizes}
                alt={alt}
                className={cn(
                    "w-full h-full object-cover transition-opacity duration-700 ease-out",
                    isLoaded ? "opacity-100" : "opacity-0",
                    className
                )}
                onLoad={() => setIsLoaded(true)}
                onError={() => setError(true)}
                loading={loading || 'lazy'}
                width={width || 800}
                height={height || 600}
                decoding="async"
                {...imgProps}
                {...(fetchPriority ? { fetchpriority: fetchPriority } : {})}
            />
        </div>
    );
}

function getResponsiveSource(src: string) {
    if (!src.includes('images.unsplash.com')) {
        return { src, srcSet: undefined, sizes: undefined };
    }

    const withWidth = (width: number) => {
        const url = new URL(src);
        url.searchParams.set('w', String(width));
        url.searchParams.set('q', '75');
        url.searchParams.set('auto', 'format');
        url.searchParams.set('fit', 'crop');
        return url.toString();
    };

    return {
        src: withWidth(640),
        srcSet: [320, 480, 640, 960, 1280].map(width => `${withWidth(width)} ${width}w`).join(', '),
        sizes: '(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw'
    };
}
